import type { GetLayers } from 'actions/surveymanager';
import { parseTenantIdSlug } from 'utils/tenant';
import { getSurveyManagerStructure, type SurveyManagerTreeNode } from './getSurveyManagerStructure';

export type SurveyManagerCatalogueActions = {
  readonly getLayers: (
    refreshData?: boolean,
    options?: { client?: string; surveyType?: string; year?: number },
  ) => Promise<GetLayers>;
  readonly getSurveyShapefiles: (
    slug: string,
    refreshData?: boolean,
    options?: { category?: string },
  ) => Promise<{ shapefiles: ReadonlyArray<{ filePath?: string }> }>;
};

export type SurveyManagerCatalogueCallbacks = {
  readonly onLayerTypesLoaded: (layerTypes: readonly string[]) => void;
  readonly onTreeUpdated: (tree: SurveyManagerTreeNode[]) => void;
};

type ShapefilePathMap = Record<string, Record<string, string | undefined>>;

let inFlightCatalogueLoad: Promise<SurveyManagerTreeNode[]> | null = null;

const buildAvailableLayerTypes = (layers: GetLayers): string[] =>
  Array.from(
    new Set(
      (layers.layers || [])
        .map((layer) => {
          const surveyType = (layer.surveyType || '').trim().toLowerCase();
          const layerName = (layer.layer || '').trim().toLowerCase();
          return `${surveyType}::${layerName}`;
        })
        .filter(Boolean),
    ),
  );

const buildCoverageShapefileTasks = (
  layers: GetLayers,
): ReadonlyArray<{ readonly type: string; readonly year: string; readonly surveySlug: string }> => {
  const firstSurveyMap = new Map<string, Map<string, string>>();
  for (const layer of layers.layers) {
    const type = (layer.surveyType ?? 'Unknown').toLowerCase();
    const year = layer.year;
    const first =
      Array.isArray(layer.surveys) && layer.surveys.length > 0 ? layer.surveys[0] : undefined;
    if (!first) continue;
    if (!firstSurveyMap.has(type)) firstSurveyMap.set(type, new Map());
    const yearMap = firstSurveyMap.get(type)!;
    if (!yearMap.has(year)) yearMap.set(year, first);
  }

  return Array.from(firstSurveyMap.entries()).flatMap(([type, yearMap]) =>
    Array.from(yearMap.entries()).map(([year, surveySlug]) => ({ type, year, surveySlug })),
  );
};

/**
 * Loads Survey Image Viewer catalogue progressively:
 * 1) emit tree as soon as /layers returns (entries visible, endpoints pending)
 * 2) fill coverage endpoints as shapefile lookups complete
 */
export async function loadSurveyManagerCatalogue(
  actions: SurveyManagerCatalogueActions,
  callbacks: SurveyManagerCatalogueCallbacks,
  options: { readonly forceRefresh?: boolean } = {},
): Promise<SurveyManagerTreeNode[]> {
  if (inFlightCatalogueLoad && !options.forceRefresh) {
    return inFlightCatalogueLoad;
  }

  const run = async (): Promise<SurveyManagerTreeNode[]> => {
    const currentTenant = parseTenantIdSlug(localStorage.getItem('tenantId'));
    const requestOptions = currentTenant ? { client: currentTenant } : {};
    const fetchedLayers = await actions.getLayers(!!options.forceRefresh, requestOptions);

    if (!fetchedLayers?.layers) {
      callbacks.onLayerTypesLoaded([]);
      callbacks.onTreeUpdated([]);
      return [];
    }

    callbacks.onLayerTypesLoaded(buildAvailableLayerTypes(fetchedLayers));

    const shapefilePathMap: ShapefilePathMap = {};
    let latestTree = getSurveyManagerStructure(fetchedLayers, shapefilePathMap);
    callbacks.onTreeUpdated(latestTree);

    const tasks = buildCoverageShapefileTasks(fetchedLayers);
    await Promise.all(
      tasks.map(async ({ type, year, surveySlug }) => {
        let filePath: string | undefined;
        try {
          const survey = await actions.getSurveyShapefiles(surveySlug, false, {
            category: 'coverage',
          });
          filePath = survey.shapefiles[0]?.filePath;
        } catch (err) {
          console.error('getSurvey/getShapefile failed for', surveySlug, err);
        }

        shapefilePathMap[type] = shapefilePathMap[type] ?? {};
        shapefilePathMap[type][year] = filePath;
        latestTree = getSurveyManagerStructure(fetchedLayers, { ...shapefilePathMap });
        callbacks.onTreeUpdated(latestTree);
      }),
    );

    return latestTree;
  };

  inFlightCatalogueLoad = run().finally(() => {
    inFlightCatalogueLoad = null;
  });

  return inFlightCatalogueLoad;
}

export function resetSurveyManagerCatalogueLoadForTests(): void {
  inFlightCatalogueLoad = null;
}
